import React, { useState, useRef, useEffect } from 'react';
import { useViewport } from './hooks/useViewport';
import { useMobileSidebar } from '../contexts/MobileSidebarContext';
import useDossierPresence from './hooks/useDossierPresence';
import ThemeSelector from '../../components/shared/ThemeSelector';
import MacadamiaSwitcher from './MacadamiaSwitcher';
import AddFavorite from './AddFavorite';
import LastSavedDate from './LastSavedDate';
import JuridicalReferencesCounter from './JuridicalReferencesCounter';
import ConnectedUsersIndicator from './ConnectedUsersIndicator';
import FloppyDisk from '../icons/FloppyDisk';
import Cog6Tooth from '../icons/Cog6Tooth';
import ClockRotate from '../icons/ClockRotate';
import ShareButton from './ShareButton';
import Bars3 from '../icons/Bars3';
import ArrowDownTray from '../icons/ArrowDownTray';
import DossierHistoryDropdown from './DossierHistoryDropdown';
import ExportDropdown from './ExportDropdown';

interface TopBarDossierProps {
  toggleSettingsModal: (tab?: string) => void;
  isSettingsModalOpen: boolean;
  title: string;
  onTitleChange: (newTitle: string) => void;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  handleSave: () => void;
  lastSaved: Date;
  dossierId: string;
  userRole: string | null;
  onShowReferences?: () => void;
  onVersionPreview?: (versionNumber: number, snapshot: any) => void;
  /**
   * Appelé par DossierHistoryDropdown après une restauration réussie.
   * Reçoit le dossier complet renvoyé par l'API. Si non fournie,
   * fallback sur window.location.reload() pour préserver le
   * comportement legacy en cas de montage hors DossierEditor.
   */
  onRestoreComplete?: (restoredDossier?: import('./widgets/types').IDossier) => void;
}

// Composant pour le titre avec tooltip
const TruncatedTitle: React.FC<{ title: string, className?: string }> = ({ title, className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  // Vérifier si le titre est tronqué (s'il dépasse la largeur du conteneur)
  const isTitleTruncated = () => {
    const element = titleRef.current;
    return element ? element.scrollWidth > element.clientWidth : false;
  };

  // Ajuster la position du tooltip pour éviter qu'il ne sorte de l'écran
  useEffect(() => {
    if (showTooltip && tooltipRef.current) {
      const tooltipElement = tooltipRef.current;
      const viewportWidth = window.innerWidth;
      const tooltipRect = tooltipElement.getBoundingClientRect();
      
      if (tooltipRect.right > viewportWidth) {
        const overflow = tooltipRect.right - viewportWidth;
        tooltipElement.style.transform = `translate(calc(-50% - ${overflow + 20}px), 0)`;
      } else if (tooltipRect.left < 0) {
        const overflow = Math.abs(tooltipRect.left);
        tooltipElement.style.transform = `translate(calc(-50% + ${overflow + 20}px), 0)`;
      }
    }
  }, [showTooltip]);

  return (
    <div 
      className={`relative ${className}`}
      onMouseEnter={() => isTitleTruncated() && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div 
        ref={titleRef}
        className="w-[300px] overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {title}
      </div>
      
      {showTooltip && (
        <div 
          ref={tooltipRef}
          className="absolute left-1/2 transform -translate-x-1/2 top-full mt-1 
                    bg-white dark:bg-mcdm-third-500 text-mcdm-third-500 dark:text-white 
                    px-4 py-2 rounded shadow-lg z-50 text-sm whitespace-normal w-[300px]"
        >
          {title}
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 
                        border-8 border-transparent border-b-white dark:border-b-mcdm-third-500" />
        </div>
      )}
    </div>
  );
};

const TopBarDossier: React.FC<TopBarDossierProps> = ({
  toggleSettingsModal,
  title,
  hasUnsavedChanges,
  isSaving,
  handleSave,
  lastSaved,
  dossierId,
  userRole,
  onShowReferences,
  onVersionPreview,
  onRestoreComplete
}) => {
  const { isMobile } = useViewport();
  const { toggleSidebar } = useMobileSidebar();
  const { userCount } = useDossierPresence(dossierId);
  const canEdit = userRole === 'author' || userRole === 'co-author' || userRole === 'admin';

  // État et gestion du dropdown historique
  const [showHistory, setShowHistory] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);

  // État et gestion du dropdown export
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Fermer les dropdowns au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setShowExport(false);
      }
    };

    if (showHistory || showExport) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHistory, showExport]);

  if (isMobile) {
    return (
      <div className="fixed top-0 h-12 bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-md z-9 w-full flex items-center px-4">
        <div className="flex items-center w-1/2">
          <button
            onClick={toggleSidebar}
            className="mr-3 text-mcdm-third-400 dark:text-mcdm-secondary-400 flex-shrink-0"
          >
            <Bars3
              width="24px"
              height="24px"
            />
          </button>
          <TruncatedTitle 
            title={title} 
            className="text-sm font-normal text-mcdm-third-500 dark:text-mcdm-primary-100"
          />
        </div>

        <div className="flex items-center justify-end w-1/2 space-x-2">
          <ThemeSelector />
          <div className="transform -translate-y-px">
            <AddFavorite dossierId={dossierId} />
          </div>
          {canEdit && (
            <div
              className="p-1 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 cursor-pointer text-mcdm-third-500 dark:text-mcdm-secondary-400 dark:hover:text-mcdm-primary-100"
              onClick={() => toggleSettingsModal('Général')}
            >
              <Cog6Tooth width="24px" height="24px" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="stickyheadbar top-0 flex-shrink-0 h-16 bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-sm dark:shadow-inner z-30 w-full flex justify-between items-center px-4">
      <div className="flex justify-start" style={{ width: '40%' }}>
        <TruncatedTitle 
          title={title} 
          className="text-sm font-normal text-mcdm-third-500 dark:text-mcdm-primary-100 p-2"
        />
      </div>

      <div className="flex justify-end items-center" style={{ width: '60%' }}>
        <JuridicalReferencesCounter
          className="mr-3"
          onClick={onShowReferences}
        />
        <ConnectedUsersIndicator
          userCount={userCount}
          className="mr-3"
        />
        <LastSavedDate lastSaved={lastSaved} />
        <div className='mr-1'>
          <ThemeSelector />
        </div>
        <div className="mr-2 mt-1" style={{ transform: 'translateY(-1px)' }}>
          <AddFavorite dossierId={dossierId} />
        </div>
        {canEdit && (
          <>
            <div className="p-1 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 mr-2 cursor-pointer hover:text-mcdm-third-500 dark:text-mcdm-secondary-400 dark:hover:text-mcdm-primary-100" onClick={() => toggleSettingsModal('Général')}>
              <Cog6Tooth width="24px" height="24px" />
            </div>
            <div className="relative" ref={historyRef}>
              <div
                className="p-1 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 mr-2 cursor-pointer text-mcdm-third-500 dark:text-mcdm-secondary-400 hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100"
                onClick={() => setShowHistory(!showHistory)}
                title="Historique des révisions"
              >
                <ClockRotate width="24px" height="24px" />
              </div>
              <DossierHistoryDropdown
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                dossierId={dossierId}
                userRole={userRole}
                onRestoreComplete={
                  onRestoreComplete ?? (() => window.location.reload())
                }
                onVersionPreview={onVersionPreview}
              />
            </div>
            <div className="relative" ref={exportRef}>
              <div
                className="p-1 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 mr-2 cursor-pointer text-mcdm-third-500 dark:text-mcdm-secondary-400 hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100"
                onClick={() => setShowExport(!showExport)}
                title="Exporter le dossier"
              >
                <ArrowDownTray width="24px" height="24px" />
              </div>
              <ExportDropdown
                isOpen={showExport}
                onClose={() => setShowExport(false)}
                dossierId={dossierId}
              />
            </div>
            <div className={`mr-2 cursor-pointer p-1 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 ${hasUnsavedChanges ? 'text-mcdm-secondary-400 dark:text-mcdm-secondary-400' : 'text-mcdm-third-500 dark:text-mcdm-primary-100'} ${isSaving ? 'animate-spin' : ''}`}
              onClick={handleSave}
            >
              <FloppyDisk width="24px" height="24px" />
            </div>
          </>
        )}
        {canEdit && <ShareButton className="ml-2" onShareClick={() => toggleSettingsModal('Visibilité')} />}
        {canEdit && <MacadamiaSwitcher className="ml-2" />}
      </div>
    </div>
  );
};

export default TopBarDossier;